// Tile rules the renderer needs, from the tile catalog: solidity, joins,
// liquids, and the per-tile hash that picks variants.
import { content } from '../api';

export const CHUNK = 32;
export const TS = 16;

export interface TileLight { radius: number; color: number; x: number; y: number; flicker: boolean }

export class TileRules {
  readonly groundSprite: (string | null)[] = new Array(65536).fill(null);
  readonly groundMaterial: number[] = new Array(65536).fill(0);
  readonly groundAnimated: boolean[] = new Array(65536).fill(false);
  /** Lights per layer (null = none): radius in pixels, colour, the row it shines from, and whether it flickers (animated tiles). */
  readonly groundLight: (TileLight | null)[] = new Array(65536).fill(null);
  readonly objectLight: (TileLight | null)[] = new Array(65536).fill(null);
  readonly objectSprite: (string | null)[] = new Array(65536).fill(null);
  readonly objectAnimated: boolean[] = new Array(65536).fill(false);
  readonly objectSolid: boolean[] = new Array(65536).fill(false);
  /** Flat objects lie on the ground; the rest are depth-sorted with characters at their sort line (pixels down from the
   * top of the object's tile). */
  readonly objectFlat: boolean[] = new Array(65536).fill(false);
  readonly objectSortY: number[] = new Array(65536).fill(TS);
  readonly objectJoins: boolean[] = new Array(65536).fill(false);
  private readonly joinGroup: number[] = new Array(65536).fill(0);
  private readonly joinMask: Set<number>[] = Array.from({ length: 65536 }, () => new Set<number>());
  readonly named: Record<string, number>;

  constructor() {
    this.named = content.named;
    const groups = new Map<string, number>();
    const group = (name: string) => {
      const key = name.toLowerCase();
      if (!groups.has(key)) groups.set(key, Math.min(groups.size + 1, 63));
      return groups.get(key)!;
    };
    for (const t of content.tiles) {
      const color = /^#[0-9a-f]{6}$/i.test(t.lightColor) ? parseInt(t.lightColor.slice(1), 16) : 0xffffff;
      const light = t.lightRadius > 0 ? { radius: t.lightRadius, color, x: t.lightX, y: t.lightY, flicker: t.animated } : null;
      (t.layer === 'ground' ? this.groundLight : this.objectLight)[t.id] = light;
      if (t.layer === 'ground') {
        this.groundSprite[t.id] = t.sprite;
        this.groundMaterial[t.id] = t.baseGround || t.id;
        this.groundAnimated[t.id] = t.animated;
        continue;
      }
      this.objectSprite[t.id] = t.sprite;
      this.objectAnimated[t.id] = t.animated;
      this.objectSolid[t.id] = t.solid;
      this.objectFlat[t.id] = t.flat;
      this.objectSortY[t.id] = t.sortY;
      if (!t.join) continue;
      this.objectJoins[t.id] = true;
      this.joinGroup[t.id] = group(t.join);
      this.joinMask[t.id].add(group(t.join));
      for (const other of t.joinsWith) this.joinMask[t.id].add(group(other));
    }
  }

  /** Whether a joining object (a wall piece) joins the neighbour next to it. */
  joins(self: number, neighbour: number): boolean {
    const g = this.joinGroup[neighbour];
    return g !== 0 && this.joinMask[self].has(g);
  }

  isLiquid(g: number): boolean {
    const base = this.groundMaterial[g] || g;
    return base === this.named.groundWater || base === this.named.groundDeepWater || base === this.named.groundLava;
  }
}

/** The per-tile hash that picks variants (uint32 arithmetic). */
export function tileHash(x: number, y: number): number {
  let h = (Math.imul(x, 374761393) + Math.imul(y, 668265263)) >>> 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177) >>> 0;
  return (h ^ (h >>> 16)) >>> 0;
}
